<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { deleteUsuario, listUsuarios } from '@/api/usuarios'
import { getApiErrorMessage } from '@/api/client'
import { useAuth } from '@/composables/useAuth'
import type { Usuario, UsuarioTipo } from '@/types/api'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const router = useRouter()
const { usuario: usuarioLogado } = useAuth()

const usuarios = ref<Usuario[]>([])
const loading = ref(true)
const tipoFilter = ref<'' | UsuarioTipo>('')
const ativoFilter = ref<'' | 'ativo' | 'inativo'>('')

const TIPO_ORDER: Record<UsuarioTipo, number> = {
  Master: 0,
  Admin: 1,
}

const tipoFilterOptions = computed(() => {
  const options: Array<{ value: '' | UsuarioTipo; label: string }> = [
    { value: '', label: 'Todos' },
  ]

  if (usuarioLogado.value?.tipo === 'Master') {
    options.push({ value: 'Master', label: 'Master' })
  }

  options.push({ value: 'Admin', label: 'Admin' })

  return options
})

const ATIVO_FILTER_OPTIONS: Array<{ value: '' | 'ativo' | 'inativo'; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'ativo', label: 'Ativos' },
  { value: 'inativo', label: 'Inativos' },
]

const usuariosFiltrados = computed(() => {
  let items = [...usuarios.value]

  if (tipoFilter.value !== '') {
    items = items.filter((usuario) => usuario.tipo === tipoFilter.value)
  }

  if (ativoFilter.value === 'ativo') {
    items = items.filter((usuario) => usuario.ativo)
  } else if (ativoFilter.value === 'inativo') {
    items = items.filter((usuario) => !usuario.ativo)
  }

  return items.sort((a, b) => {
    const tipoDiff = TIPO_ORDER[a.tipo] - TIPO_ORDER[b.tipo]
    if (tipoDiff !== 0) {
      return tipoDiff
    }

    return a.nome.localeCompare(b.nome, 'pt-BR')
  })
})

async function load(): Promise<void> {
  loading.value = true

  try {
    usuarios.value = await listUsuarios()
  } catch (err) {
    usuarios.value = []
    await Swal.fire({
      icon: 'error',
      title: 'Erro ao carregar',
      text: getApiErrorMessage(err, 'Não foi possível carregar os usuários'),
    })
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await load()
})

function isSelf(id: string): boolean {
  return usuarioLogado.value?.id === id
}

async function onExcluir(usuario: Usuario): Promise<void> {
  if (isSelf(usuario.id)) {
    await Swal.fire({
      icon: 'warning',
      title: 'Não permitido',
      text: 'Você não pode excluir o próprio usuário.',
    })
    return
  }

  const result = await Swal.fire({
    icon: 'warning',
    title: 'Excluir usuário?',
    text: `${usuario.nome} será removido. Esta ação não pode ser desfeita.`,
    showCancelButton: true,
    confirmButtonText: 'Excluir',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#dc3545',
  })

  if (!result.isConfirmed) {
    return
  }

  try {
    await deleteUsuario(usuario.id)
    await load()
    await Swal.fire({
      icon: 'success',
      title: 'Usuário excluído',
      timer: 1500,
      showConfirmButton: false,
    })
  } catch (err) {
    await Swal.fire({
      icon: 'error',
      title: 'Não foi possível excluir',
      text: getApiErrorMessage(err, 'Erro ao excluir usuário'),
    })
  }
}

function goToEdit(id: string): void {
  router.push(`/admin/usuarios/${id}`)
}

function tipoBadgeClass(tipo: UsuarioTipo): string {
  if (tipo === 'Master') {
    return 'text-bg-dark'
  }
  if (tipo === 'Admin') {
    return 'text-bg-primary'
  }
  return 'text-bg-secondary'
}
</script>

<template>
  <AdminLayout>
    <template #title>
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h1 class="h5 mb-1">Usuários</h1>
          <p class="text-muted small mb-0">Gerencie acessos Master e Admin.</p>
        </div>
        <RouterLink class="btn btn-dark btn-sm" to="/admin/usuarios/novo">
          <i class="bi bi-plus-lg me-1" aria-hidden="true" />
          Novo usuário
        </RouterLink>
      </div>
    </template>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-dark" role="status">
        <span class="visually-hidden">Carregando…</span>
      </div>
    </div>

    <div v-else class="card border-0 shadow-sm">
      <div class="card-body p-4">
        <div class="row g-3 mb-4">
          <div class="col-12 col-sm-6 col-md-4">
            <label class="form-label small text-muted mb-1" for="filtro_tipo">Tipo</label>
            <select
              id="filtro_tipo"
              v-model="tipoFilter"
              class="form-select form-select-sm"
            >
              <option
                v-for="option in tipoFilterOptions"
                :key="option.label"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <div class="col-12 col-sm-6 col-md-4">
            <label class="form-label small text-muted mb-1" for="filtro_ativo">Situação</label>
            <select
              id="filtro_ativo"
              v-model="ativoFilter"
              class="form-select form-select-sm"
            >
              <option
                v-for="option in ATIVO_FILTER_OPTIONS"
                :key="option.label"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="usuariosFiltrados.length === 0" class="text-center py-5">
          <i class="bi bi-people display-4 text-muted" aria-hidden="true" />
          <p class="text-muted mt-3 mb-0">Nenhum usuário encontrado.</p>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th scope="col">Nome</th>
                <th scope="col">Login</th>
                <th scope="col">Tipo</th>
                <th scope="col">Situação</th>
                <th scope="col" class="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="usuario in usuariosFiltrados" :key="usuario.id">
                <td>
                  {{ usuario.nome }}
                  <span v-if="isSelf(usuario.id)" class="text-muted small">(você)</span>
                </td>
                <td>{{ usuario.login }}</td>
                <td>
                  <span class="badge rounded-pill" :class="tipoBadgeClass(usuario.tipo)">
                    {{ usuario.tipo }}
                  </span>
                </td>
                <td>
                  <span
                    class="badge rounded-pill"
                    :class="usuario.ativo ? 'text-bg-success' : 'text-bg-secondary'"
                  >
                    {{ usuario.ativo ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td class="text-end">
                  <div class="btn-group usuarios-list__acoes">
                    <button
                      type="button"
                      class="btn btn-outline-secondary"
                      title="Editar"
                      @click="goToEdit(usuario.id)"
                    >
                      <i class="bi bi-pencil" aria-hidden="true" />
                      <span class="visually-hidden">Editar</span>
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-danger"
                      title="Excluir"
                      :disabled="isSelf(usuario.id)"
                      @click="onExcluir(usuario)"
                    >
                      <i class="bi bi-trash" aria-hidden="true" />
                      <span class="visually-hidden">Excluir</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<style scoped>
.usuarios-list__acoes .btn {
  min-width: 2.75rem;
  min-height: 2.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
}
</style>
